/** Thin fetch wrapper. Same origin only; the custom header is our CSRF defence. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(code);
  }
}

/** Thrown when the request never reached the server (offline, DNS, TLS, timeout). */
export class NetworkError extends Error {}

export async function api<T = unknown>(method: string, url: string, body?: unknown, opts: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 20_000);
  opts.signal?.addEventListener('abort', () => ctrl.abort());
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(method !== 'GET' ? { 'x-tc': '1' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : 'network');
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const d = data as { error?: string; details?: unknown } | null;
    if (res.status === 403 && d?.error === 'consent_required') window.dispatchEvent(new Event('tc:consent-required'));
    throw new ApiError(res.status, d?.error ?? `http_${res.status}`, d?.details);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** POST a binary body (audio). Same error handling as api(). */
export async function apiUpload<T = unknown>(url: string, body: Blob, contentType: string, timeoutMs = 60_000): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': contentType, 'x-tc': '1' },
      body,
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : 'network');
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const d = data as { error?: string; details?: unknown } | null;
    throw new ApiError(res.status, d?.error ?? `http_${res.status}`, d?.details);
  }
  return data as T;
}
